type Caller = { resolve: () => void; reject: (error: unknown) => void }

/**
 * Runs saves one at a time, so they can't land out of order.
 *
 * A save asked for while another is in flight waits. If several are waiting,
 * only the latest values are sent: each save sends the whole lead, so the
 * latest values include every earlier change.
 *
 * Each call resolves when the save carrying its values lands, and rejects if
 * that save fails. A failed save doesn't stop the values waiting behind it
 * from being sent.
 *
 * `onLatestSaved` is called with a save's result only when nothing is waiting
 * behind it. A save that lands while newer values are waiting holds older
 * values than the user has now, so its result shouldn't be shown.
 *
 * `isSaving` is true while a save is in flight or waiting, and `whenIdle`
 * resolves once none are, whether the last save succeeded or failed.
 * `sentCount` is how many saves have been sent so far, so a caller can tell
 * whether one was sent while it was doing something else.
 */
const createSaveQueue = <Values, Result>(
  send: (values: Values) => Promise<Result>,
  onLatestSaved?: (result: Result) => void
): {
  save: (values: Values) => Promise<void>
  isSaving: () => boolean
  whenIdle: () => Promise<void>
  sentCount: () => number
} => {
  let isSaving = false
  let sentCount = 0
  let waiting: { values: Values; callers: Caller[] } | null = null
  let idleCallers: (() => void)[] = []

  const save = async (values: Values, callers: Caller[]) => {
    isSaving = true
    sentCount += 1
    try {
      const result = await send(values)
      if (!waiting) onLatestSaved?.(result)
      callers.forEach((caller) => caller.resolve())
    } catch (error) {
      callers.forEach((caller) => caller.reject(error))
    }

    if (waiting) {
      const next = waiting
      waiting = null
      save(next.values, next.callers)
    } else {
      isSaving = false
      idleCallers.forEach((resolve) => resolve())
      idleCallers = []
    }
  }

  return {
    save: (values) =>
      new Promise((resolve, reject) => {
        const caller = { resolve, reject }
        if (isSaving) {
          waiting = { values, callers: [...(waiting?.callers ?? []), caller] }
        } else {
          save(values, [caller])
        }
      }),
    isSaving: () => isSaving,
    whenIdle: () =>
      isSaving
        ? new Promise((resolve) => idleCallers.push(resolve))
        : Promise.resolve(),
    sentCount: () => sentCount,
  }
}

export default createSaveQueue
